import React from 'react';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import Breadcrumbs from '../marketing/components/Breadcrumbs';
import KeyTakeaways from '../marketing/components/KeyTakeaways';
import ArticleBody from '../marketing/components/ArticleBody';
import FaqAccordion from '../marketing/components/FaqAccordion';
import ArticleCard from '../marketing/components/ArticleCard';
import { getArticle, getCategory, getGuide, getAuthor, cover, formatDate } from '../marketing/data';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const ArticlePage: React.FC = () => {
  useMarketingFonts();
  const { slug } = useParams<{ slug: string }>();
  const article = getArticle(slug);

  const cat = article ? getCategory(article.category) : undefined;
  const author = article ? getAuthor(article.author) : undefined;
  const guide = article?.pillarGuide ? getGuide(article.pillarGuide) : undefined;
  const related = (article?.related || []).map((s) => getArticle(s)).filter(Boolean).slice(0, 3) as NonNullable<ReturnType<typeof getArticle>>[];
  const heroImg = article ? cover(article) : '';
  const url = article ? `https://www.millos.uk/blog/${article.slug}` : '';

  useSeoHead({
    title: article ? `${article.title} | MillOS` : 'MillOS',
    description: article?.description || '',
    ogType: 'article',
    ogImage: heroImg && !heroImg.startsWith('data:') ? heroImg : undefined,
    canonicalPath: article ? `/blog/${article.slug}` : undefined,
    jsonLd: article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: article.title,
            description: article.description,
            image: heroImg,
            datePublished: article.published,
            dateModified: article.updated,
            author: { '@type': 'Organization', name: author?.name },
            publisher: { '@type': 'Organization', name: 'MillOS' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': url },
            articleSection: cat ? cat.name : article.category,
            keywords: (article.tags || []).join(', '),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: (article.faqs || []).map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.millos.uk/' },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.millos.uk/blog' },
              { '@type': 'ListItem', position: 3, name: cat ? cat.name : article.category, item: `https://www.millos.uk/blog/category/${article.category}` },
              { '@type': 'ListItem', position: 4, name: article.title, item: url },
            ],
          },
        ]
      : [],
  });

  if (!article) return <Navigate to="/blog" replace />;

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <Breadcrumbs
        items={[{ label: 'Home', to: '/' }, { label: 'Blog', to: '/blog' }, { label: cat?.name || article.category, to: `/blog/category/${article.category}` }, { label: article.title }]}
      />

      <Box component="article">
        <Box component="header" sx={{ padding: { xs: '24px 24px 0', md: '32px 52px 0' } }}>
          <Box sx={{ maxWidth: 760, margin: '0 auto' }}>
            <Box
              component={RouterLink}
              to={`/blog/category/${article.category}`}
              sx={{ display: 'inline-block', background: C.tintBlue, color: C.accent, borderRadius: '32px', padding: '6px 14px', fontSize: 11, fontWeight: 500, letterSpacing: '0.06rem', textTransform: 'uppercase' }}
            >
              {cat?.name || article.category}
            </Box>
            <Box component="h1" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2.25rem,4.6vw,3.75rem)', lineHeight: 1.08, letterSpacing: '-0.03em', marginTop: '20px' }}>
              {article.title}
            </Box>
            <Box sx={{ fontSize: 18, color: C.body, marginTop: '18px', lineHeight: 1.6 }}>{article.description}</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', marginTop: '24px', fontSize: 13.5, color: C.muted }}>
              <Box component="span" sx={{ color: C.ink, fontWeight: 500 }}>{author?.name}</Box>
              <span>·</span>
              <span>Updated {formatDate(article.updated)}</span>
              <span>·</span>
              <span>{article.readingMinutes} min read</span>
            </Box>
          </Box>
        </Box>

        <Box sx={{ padding: { xs: '24px 16px 0', md: '32px 52px 0' } }}>
          <Box sx={{ maxWidth: 1000, margin: '0 auto', borderRadius: '28px', overflow: 'hidden', aspectRatio: '16/8', background: C.panel }}>
            <Box component="img" src={heroImg} alt={article.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        </Box>

        <Box sx={{ padding: { xs: '40px 16px 0', md: '48px 52px 0' } }}>
          <Box sx={{ maxWidth: 760, margin: '0 auto' }}>
            <KeyTakeaways items={article.keyTakeaways} />
            <ArticleBody sections={article.sections} />

            {guide && (
              <Box
                component={RouterLink}
                to={`/guides/${guide.slug}`}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', background: C.inkSoft, color: C.white, borderRadius: '20px', padding: '24px 28px', marginTop: '40px' }}
              >
                <Box>
                  <Box sx={{ fontSize: 12, letterSpacing: '0.08rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>Pillar guide</Box>
                  <Box sx={{ fontFamily: F.display, fontSize: 22, marginTop: '4px' }}>{guide.title}</Box>
                </Box>
                <Box sx={{ flex: 'none', fontSize: 22 }}>→</Box>
              </Box>
            )}

            <Box sx={{ marginTop: '56px' }}>
              <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 32, letterSpacing: '-0.02em', marginBottom: '24px' }}>
                Frequently asked questions
              </Box>
              <FaqAccordion faqs={article.faqs} />
            </Box>
          </Box>
        </Box>
      </Box>

      {related.length > 0 && (
        <Box component="section" sx={{ padding: { xs: '56px 16px 40px', md: '80px 52px 40px' } }}>
          <Box sx={{ maxWidth: 1000, margin: '0 auto' }}>
            <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 32, letterSpacing: '-0.02em', marginBottom: '28px' }}>
              Related articles
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: '24px' }}>
              {related.map((r) => (
                <ArticleCard key={r.slug} article={r} />
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Box component="section" sx={{ padding: { xs: '24px 16px 0', md: '40px 52px 0' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', background: C.tintBlue, borderRadius: '28px', padding: { xs: '40px 24px', md: '56px 40px' }, textAlign: 'center' }}>
          <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(1.8rem,3.4vw,2.6rem)', letterSpacing: '-0.02em' }}>
            Put this on autopilot with MillOS
          </Box>
          <Box sx={{ maxWidth: '34rem', margin: '16px auto 0', fontSize: 16, color: C.body }}>
            MillOS tracks every certificate, chases every renewal and reconciles every payment — so you never miss a deadline again.
          </Box>
          <Box
            component={RouterLink}
            to="/register"
            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: C.inkSoft, color: C.white, borderRadius: '32px', padding: '13px 24px', fontSize: 16, marginTop: '28px', '&:hover': { background: C.accent } }}
          >
            Start free trial
          </Box>
        </Box>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default ArticlePage;
